import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, extractError } from "@/api/client";
import { idbPersister } from "@/lib/persister";

const AuthContext = createContext(null);

const USER_KEY = "kilolava.user";
const TOKEN_KEY = "access_token";

const FULL_ACCESS_ROLES = ["resp_magazzino", "resp_pulizie", "resp_consegne", "resp_ufficio"];
export const ROLE_LABEL = { super_admin: "Super Admin", resp_magazzino: "Responsabile magazzino", resp_pulizie: "Responsabile pulizie", resp_consegne: "Responsabile consegne", resp_ufficio: "Responsabile ufficio", responsabile: "Responsabile", resp_logistica: "Resp. logistica", rider: "Rider", dipendente: "Dipendente", cliente: "Cliente" };
// I 4 responsabili di area usano il gestionale «responsabili» (tutto visibile): in sessione contano come super_admin.
const normalizeUser = (u) => (u && FULL_ACCESS_ROLES.includes(u.role) ? { ...u, actual_role: u.role, role: "super_admin" } : u);

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? normalizeUser(JSON.parse(raw)) : null;
  } catch (e) {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch (e) { /* ignore quota */ }
}

export function AuthProvider({ children }) {
  // Boot ottimista: se c'è un access_token + user in cache, usiamo quelli SUBITO.
  // Un'unica validazione in background aggiorna/estende la sessione, ma NON butta
  // giù l'utente per errori transitori (rete/timeout/5xx). Solo un 401 esplicito
  // dopo refresh fallito forza un ri-login.
  const initialToken = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const initialUser = initialToken ? readCachedUser() : null;
  const [user, setUser] = useState(initialUser); // null = not authed
  const [loading, setLoading] = useState(!initialUser && !!initialToken);

  const fetchMe = useCallback(async ({ hard } = {}) => {
    try {
      const { data } = await api.get("/auth/me");
      const u = normalizeUser(data);
      setUser(u);
      writeCachedUser(u);
      return u;
    } catch (e) {
      const status = e?.response?.status;
      // Solo un 401 esplicito dal server = sessione veramente scaduta.
      // Qualunque altro errore (network, timeout, 5xx, offline) → mantieni
      // l'utente cached se ce l'abbiamo, così l'app resta usabile.
      if (status === 401) {
        writeCachedUser(null);
        try { localStorage.removeItem(TOKEN_KEY); } catch (err) { /* ignore */ }
        setUser(null);
      } else if (hard) {
        // richiesta esplicita a "azzerare" (post-logout)
        setUser(null);
      }
      // altrimenti: nessun setUser, teniamo la sessione ottimista
      return null;
    }
  }, []);

  useEffect(() => {
    // Se ho un token, valido in background senza bloccare la UI.
    if (initialToken) {
      fetchMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginWithPin = async (pin) => {
    const { data } = await api.post("/auth/login-pin", { pin });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const u = normalizeUser(data.user);
    writeCachedUser(u);
    setUser(u);
    return u;
  };

  const logout = async () => {
    // Prima svuota la cache API del service worker: così non resta nulla del
    // vecchio utente anche se qualche richiesta è ancora in volo.
    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "CLEAR_API_CACHE" });
      }
    } catch (e) { /* ignore */ }
    try {
      await api.post("/auth/logout");
    } catch (e) { /* ignore */ }
    // Wipe any per-user persisted state (chat, offline queue is kept as-is).
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("kilo.chat.v1."))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    writeCachedUser(null);
    setUser(null);
    // Butta via la copia offline dei dati del vecchio utente.
    try { await idbPersister.removeClient(); } catch (e) { /* ignore */ }
    // Ripulisci ancora: qualche risposta può essere arrivata dopo il primo wipe.
    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "CLEAR_API_CACHE" });
      }
    } catch (e) { /* ignore */ }
  };

  const value = {
    user,
    loading,
    loginWithPin,
    logout,
    refresh: fetchMe,
    extractError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
